import { AlertTriangle, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/shared/components';
import type { CleaningCompletionReportResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatNumber } from '@/shared/lib/format';
import { evidenceLabel, exportCompletionReportCsv, guestsLabel } from './completionReportModel';

/** Apartments als Tabelle — die Alternative zum Diagramm mit allen Werten und dem CSV-Export. */
export function CompletionReportApartmentTable({
  report,
  from,
  to,
  onSelectApartment,
}: {
  report: CleaningCompletionReportResponse;
  from: string;
  to: string;
  onSelectApartment: (propertyId: string) => void;
}) {
  return (
    <section
      className="border-hairline rounded-tile overflow-hidden border bg-white"
      aria-labelledby="completion-table-title"
    >
      <header className="border-hairline flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h2 id="completion-table-title" className="font-serif text-lg font-bold">
            Apartments
          </h2>
          <p className="text-ink-soft text-xs">
            Mengen und Nachweise – die Grundlage der Abrechnung.
          </p>
        </div>
        <Button
          variant="secondary"
          className="min-h-11"
          leftIcon={<Download size={15} aria-hidden />}
          onClick={() => exportCompletionReportCsv(report, from, to)}
        >
          CSV exportieren
        </Button>
      </header>
      {/* relative: der vorgelesene Spaltenkopf (sr-only, absolut) bleibt im Scrollbereich, statt die
          Seite am Handy zu verbreitern. */}
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[40rem] text-left text-sm">
          <thead className="bg-card-alt text-ink-soft font-mono text-[11px] font-semibold uppercase tracking-[0.1em]">
            <tr>
              <th className="px-4 py-2.5">Apartment</th>
              <th className="px-3 py-2.5 text-right">Reinigungen</th>
              <th className="px-3 py-2.5 text-right">Gäste</th>
              <th className="px-3 py-2.5">Fotonachweise</th>
              <th className="px-3 py-2.5">Meldungen</th>
              <th className="w-12 px-2 py-2.5">
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-hairline divide-y">
            {report.apartments.map((apartment) => {
              const name = apartment.operationalName ?? apartment.name;
              const selected = report.selectedApartment?.propertyId === apartment.propertyId;
              const totals = apartment.totals;
              const incomplete = totals.evidenceRequiredCount > totals.evidenceCompleteCount;
              return (
                <tr key={apartment.propertyId} className={cn(selected && 'bg-forest-soft/60')}>
                  <td className="px-4 py-3">
                    <b className="font-serif text-base">{name}</b>
                    <span className="text-ink-soft block text-xs">
                      {apartment.operationalName ? apartment.name : apartment.objectName}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums">
                    {formatNumber(totals.cleaningCount)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{guestsLabel(totals)}</td>
                  <td
                    className={cn(
                      'px-3 py-3 text-xs font-semibold',
                      incomplete ? 'text-sealwax' : 'text-forest',
                    )}
                  >
                    {evidenceLabel(totals)}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {totals.openIssueCount === 0 ? (
                      <span className="text-ink-soft">keine offen</span>
                    ) : (
                      <span className="text-sealwax inline-flex items-center gap-1 font-semibold">
                        <AlertTriangle size={14} aria-hidden />{' '}
                        {formatNumber(totals.openIssueCount)} offen
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      aria-pressed={selected}
                      aria-label={`${name} im Detail ansehen`}
                      onClick={() => onSelectApartment(apartment.propertyId)}
                      className="text-ink-soft hover:text-ink rounded-pill inline-flex size-11 items-center justify-center"
                    >
                      <ChevronRight size={18} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
